import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronUp, ArrowRight, Check } from 'lucide-react';
import { cn } from '../lib/utils';
import { ProgressBar } from './ProgressBar';
import type { FormStepProps } from '../types/form';

export function QuestionStep({
  question,
  value,
  onChange,
  onNext,
  onPrevious,
  isFirst,
  isLast,
  currentStep,
  totalSteps
}: FormStepProps) {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [question.id]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && value) {
      e.preventDefault();
      onNext();
    }
  };

  const isCompleted = value.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      className="min-h-screen flex items-center justify-center p-6"
    >
      <div className="max-w-2xl w-full space-y-8">
        <ProgressBar 
          currentStep={currentStep} 
          totalSteps={totalSteps}
          className="mb-12" 
        />

        <div className="space-y-6">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center space-x-3"
          >
            <h2 className="text-2xl font-medium dark:text-white font-display">{question.label}</h2>
            {isCompleted && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
              >
                <Check className="w-6 h-6 text-green-500" />
              </motion.div>
            )}
          </motion.div>

          <div className="space-y-2">
            <p className="text-gray-600 dark:text-gray-300">{question.description}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">Example: {question.example}</p>
          </div>

          {question.type === 'textarea' ? (
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyPress={handleKeyPress}
              className={cn(
                "w-full px-4 py-3 rounded-lg border-2",
                "border-gray-200 dark:border-gray-700",
                "bg-white dark:bg-gray-800",
                "text-gray-900 dark:text-white",
                "focus:border-[#60a3bc] focus:ring-2 focus:ring-[#60a3bc] focus:ring-opacity-50",
                "transition-colors duration-200",
                "min-h-[120px] resize-none"
              )}
              placeholder="Type your answer here..."
            />
          ) : question.type === 'date' ? (
            <input
              type="date"
              ref={inputRef as React.RefObject<HTMLInputElement>}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyPress={handleKeyPress}
              className={cn(
                "w-full px-4 py-3 rounded-lg border-2",
                "border-gray-200 dark:border-gray-700",
                "bg-white dark:bg-gray-800",
                "text-gray-900 dark:text-white",
                "focus:border-[#60a3bc] focus:ring-2 focus:ring-[#60a3bc] focus:ring-opacity-50",
                "transition-colors duration-200"
              )}
            />
          ) : (
            <input
              type="text"
              ref={inputRef as React.RefObject<HTMLInputElement>}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyPress={handleKeyPress}
              className={cn(
                "w-full px-4 py-3 rounded-lg border-2",
                "border-gray-200 dark:border-gray-700",
                "bg-white dark:bg-gray-800",
                "text-gray-900 dark:text-white",
                "focus:border-[#60a3bc] focus:ring-2 focus:ring-[#60a3bc] focus:ring-opacity-50",
                "transition-colors duration-200"
              )}
              placeholder="Type your answer here..."
            />
          )}

          <div className="flex justify-between pt-4">
            {!isFirst && (
              <button
                onClick={onPrevious}
                className="flex items-center space-x-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                <ChevronUp className="w-5 h-5" />
                <span>Previous</span>
              </button>
            )}
            
            <button
              onClick={onNext}
              disabled={!value && question.required}
              className={cn(
                "flex items-center space-x-2 px-6 py-3 rounded-full",
                "transition-colors duration-200",
                value
                  ? "bg-[#60a3bc] text-white hover:bg-[#4a8ba3]"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed"
              )}
            >
              <span>{isLast ? 'Preview' : 'Next'}</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}